import { SYSTEM_CONFIG } from "@/config/app.config";
import { isNull } from "lodash-es";

class SyncStorage {
  constructor(option = {}) {
    /** init options */
    this.storage = this.getStorage(option.storage || SYSTEM_CONFIG.storage);
    this.isStorageWritable = false;
    this.prefix = option.prefix || SYSTEM_CONFIG.prefix;
    this.user = "user";
    this.token = "token";
    this.loginAs = "loginAs";

    /* console.log(
      "[pinia.SyncStorage] option:",
      option.storage || SYSTEM_CONFIG.storage,
      this.prefix,
      option,
    ); */
  }

  /**
   * Pinia subscribe plugin function.
   * @param {Object} store Pinia instance
   */
  subscribe(store) {
    const storageStatus = this.checkStorage();

    if (!storageStatus.available) {
      console.warn("[Pinia.SyncStorage] Storage is unavailable; sync disabled");
      return;
    }

    this.isStorageWritable = storageStatus.writable;

    /* if (this.checkVersion(SYSTEM_CONFIG.version)) {
      console.log(
        `[Pinia.SyncStorage] Current version of the application "${SYSTEM_CONFIG.version}"`,
      );
    } else {
      console.warn(
        `[Pinia.SyncStorage] Application version updated to "${SYSTEM_CONFIG.version}"`,
      );
    } */

    // init and apply user state from storage
    this.initUserState(store);

    if (!this.isStorageWritable) {
      return;
    }

    store.$subscribe((mutation, state) => {
      // console.log("storage subscribe", mutation.type);
      if (mutation.storeId === "user") {
        if (!isNull(state.token)) {
          this.setToStorage(`${this.prefix}${this.token}`, state.token);
        }
        this.setToStorage(`${this.prefix}${this.user}`, JSON.stringify(state.user));
        this.setToStorage(`${this.prefix}${this.loginAs}`, state.loginAs);
      }
    });
  }

  getStorage(storageName) {
    if (typeof window === "undefined") {
      return null;
    }

    const storage = window[storageName];

    if (
      !storage ||
      typeof storage.getItem !== "function" ||
      typeof storage.setItem !== "function" ||
      typeof storage.removeItem !== "function"
    ) {
      return null;
    }

    return storage;
  }

  /**
   * Check LocalStorage to read/write.
   */
  checkStorage() {
    if (!this.storage) {
      return {
        available: false,
        writable: false,
      };
    }

    try {
      this.storage.getItem(`${this.prefix}@@`);
    } catch (err) {
      console.warn(`[Pinia.SyncStorage] Read storage failed: ${err}`);
      return {
        available: false,
        writable: false,
      };
    }

    try {
      this.storage.setItem(`${this.prefix}@@`, 1);
      this.storage.removeItem(`${this.prefix}@@`);
    } catch (err) {
      console.warn(`[Pinia.SyncStorage] Storage sync is read-only: ${err}`);
      return {
        available: true,
        writable: false,
      };
    }

    return {
      available: true,
      writable: true,
    };
  }

  /**
   * Check application version.
   * If the version of the application has changed, then the storage is cleared of the session
   * and settings. Required to apply the settings of the new version of the application
   * @param {String} version version of the application
   */
  checkVersion(version) {
    try {
      if (this.getFromStorage(`${this.prefix}version`) === version) {
        return true;
      }

      this.storage.clear();
      this.setToStorage(`${this.prefix}version`, version);
    } catch (err) {
      console.error(`[pinia.SyncStorage] Check version failed: ${err}`);
    }
    return false;
  }

  /**
   * Get user info from storage.
   */
  initUserState(store) {
    const userState = this.getFromStorage(`${this.prefix}${this.user}`);
    const token = this.getFromStorage(`${this.prefix}${this.token}`) || null;
    const loginAs = this.getFromStorage(`${this.prefix}${this.loginAs}`) || null;
    if (userState) {
      let storageUserInfo = null;

      try {
        storageUserInfo = JSON.parse(userState);
      } catch (err) {
        console.warn(`[Pinia.SyncStorage] Parse user state failed: ${err}`);
        this.removeFromStorage(`${this.prefix}${this.user}`);
        return false;
      }

      store.$state.token = storageUserInfo.jwt?.token || token;
      store.$state.role = storageUserInfo.role;
      store.$state.user = storageUserInfo;
      store.$state.name = storageUserInfo.name;
      store.$state.restaurantName = storageUserInfo.restaurant?.name;
      store.$state.restaurantID = storageUserInfo.restaurant?.id;
      store.$state.outletType = storageUserInfo.restaurant?.outletType;
      store.$state.restaurantInfo = storageUserInfo.restaurant;
      store.$state.loginAs = loginAs;
      return true;
    }

    return false;
  }

  /**
   * Get data to storage.
   * @param {String} key
   * @param {String} value
   */
  setToStorage(key, value) {
    if (!this.isStorageWritable) {
      return false;
    }

    try {
      this.storage.setItem(key, value);
    } catch (err) {
      this.isStorageWritable = false;
      console.warn(`[Pinia.SyncStorage] setItem storage failed: ${err}`);
      return false;
    }
    return true;
  }

  removeFromStorage(key) {
    if (!this.storage) {
      return false;
    }

    try {
      this.storage.removeItem(key);
    } catch (err) {
      console.warn(`[Pinia.SyncStorage] removeItem storage failed: ${err}`);
      return false;
    }

    return true;
  }

  /**
   * Get data from storage.
   * @param {String} key
   */
  getFromStorage(key) {
    try {
      return this.storage.getItem(key);
    } catch (err) {
      console.error(`[Pinia.SyncStorage] getItem storage failed: ${err}`);
    }
    return "";
  }
}

export default function (store, syncStorageOption) {
  const syncStorage = new SyncStorage(syncStorageOption);
  return syncStorage.subscribe(store);
}
